/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { formatDocForDisplay } from '../../../utils/format';
import {
   isAptToIssue,
   issuanceBlockReason,
} from '../../Finance/utils/bankAccount';

// ---------------------------------------------------------------------------
// Escolha da EMPRESA EMISSORA na criação de cobrança.
//
// Era um <select> no meio do formulário. Com dois CNPJs, emitir pela empresa
// errada é um erro caro (cancelar + reemitir) e silencioso — nada na tela
// gritava qual empresa ia faturar. Vira cartão de rádio: apelido, titular e
// CNPJ visíveis o tempo todo, sem precisar abrir nada.
//
// Contas inaptas (sem chave de integração ativa) aparecem desabilitadas com o
// motivo — o operador entende por que não pode escolhê-las, em vez de descobrir
// no 422 do submit.
// ---------------------------------------------------------------------------

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
   gap: 10px;
`;

const Card = styled.button`
   text-align: left;
   display: flex;
   flex-direction: column;
   gap: 3px;
   padding: 12px 14px;
   border-radius: 12px;
   cursor: pointer;
   font-family: inherit;

   border: 2px solid
      ${({ $selected, theme }) =>
         $selected
            ? theme?.colors?.actionWrite ?? '#4098ef'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $selected }) =>
      $selected ? 'rgba(64,152,239,0.08)' : '#fff'};

   &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background: rgba(0, 0, 0, 0.03);
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme?.colors?.focusRing ?? 'rgba(64,152,239,0.4)'};
   }

   .name {
      font-size: 0.9rem;
      font-weight: 800;
      display: flex;
      align-items: center;
      gap: 6px;
   }

   .owner {
      font-size: 0.72rem;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }

   .doc {
      font-size: 0.78rem;
      font-variant-numeric: tabular-nums;
      font-weight: 600;
   }

   .blocked {
      margin-top: 4px;
      font-size: 0.68rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: #854f0b;
   }

   .check {
      color: ${({ theme }) => theme?.colors?.actionWrite ?? '#4098ef'};
   }
`;

export default function BankAccountCards({
   accounts,
   selectedId,
   onSelect,
   disabled = false,
}) {
   return (
      <Grid>
         {accounts.map((account) => {
            const blockReason = issuanceBlockReason(account);
            const isBlocked = !isAptToIssue(account);
            const isSelected = String(account.id) === String(selectedId);

            return (
               <Card
                  key={account.id}
                  type="button"
                  $selected={isSelected}
                  disabled={disabled || isBlocked}
                  onClick={() => onSelect(String(account.id))}
                  aria-pressed={isSelected}
               >
                  <span className="name">
                     {account.account_name}
                     {isSelected && <span className="check">✓</span>}
                  </span>
                  <span className="owner">{account.account_owner_name}</span>
                  <span className="doc">
                     {formatDocForDisplay(account.account_doc_number)}
                  </span>
                  {blockReason && (
                     <span className="blocked">{blockReason}</span>
                  )}
               </Card>
            );
         })}
      </Grid>
   );
}

BankAccountCards.propTypes = {
   accounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
   selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onSelect: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
};
